import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtAbsenceService} from '@app/services/gt-absence.service';
import {environment} from '@environment';
import {AbsenceEntry, EndUser, SipTerminal} from '@gt-api';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ContactListType} from '../contacts/contacts.component';
import {GtAudioPlayerService} from '../../services/gt-audioplayer.service';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {JsSIPService} from '../../services/gt-jssip.service';
import {StatusModel} from '@app/models/status.model';
import {StatusAbsentModel} from '@app/models/status-absent.model';
import {StatusAvailableModel} from '@app/models/status-available.model';
import {GtStatusModelService} from '@app/services/gt-status-model.service';

@Component({
  selector: 'gt-webrtc-phone',
  templateUrl: './webrtc-phone.component.html',
  styleUrls: ['./webrtc-phone.component.scss']
})
export class WebRTCPhoneComponent implements OnInit, OnDestroy {
  contactListTypes = ContactListType;
  outgoingNr = '';

  callee: string;

  State = State;
  currentState = State.NO_SESSION;

  dtmfMessage = '';
  duration = 0;
  private timer;

  statusModels: Array<StatusModel>;
  currentAbsenceReason: StatusAbsentModel | StatusAvailableModel;

  private readonly destroyed = new Subject<void>();
  private endUser: EndUser;
  private absenceEntries: AbsenceEntry[] = [];

  @ViewChild('localAudio', {static: true}) local: ElementRef;
  @ViewChild('remoteAudio', {static: true}) remote: ElementRef;

  dialpadVisible = false;
  muted = false;
  onHold = false;
  contactsShown = true;
  callLogShown = false;
  dialerShown = false;

  constructor(
    private readonly notificationService: GtNotificationService,
    private readonly jsSIPService: JsSIPService,
    public authenticationService: GtAuthenticationService,
    private readonly absenceService: GtAbsenceService,
    private readonly statusModelService: GtStatusModelService,
    private readonly directoryService: GtDirectoryService,
    private readonly audioPlayer: GtAudioPlayerService
  ) {}

  ngOnInit() {
    this.statusModelService.getStatusModels(this.authenticationService.oname, this.authenticationService.login).subscribe(statusModels => {
      this.statusModels = statusModels;

      this.directoryService.getEndUser(this.authenticationService.oname, this.authenticationService.login).subscribe(endUser => {
        this.endUser = endUser;
        this.updateCurrentStatus(endUser, this.absenceEntries);
      });

      this.absenceService
        .getAbsences(this.authenticationService.oname, true)
        .pipe(takeUntil(this.destroyed))
        .subscribe(absenceEntries => {
          this.absenceEntries = absenceEntries;
          this.updateCurrentStatus(this.endUser, absenceEntries);
        });

      this.directoryService
        .getContacts(this.authenticationService.oname, true)
        .pipe(takeUntil(this.destroyed))
        .subscribe(contacts => {
          const endUser = contacts.find(c => c.login === this.authenticationService.login);
          this.endUser = endUser;
          this.updateCurrentStatus(endUser, this.absenceEntries);
        });
    });

    this.directoryService.getSipTerminal(this.authenticationService.oname, this.authenticationService.login).subscribe(sipTerminal => {
      this.initWithSipTerminal(sipTerminal);
    });
  }

  private initWithSipTerminal(s: SipTerminal) {
    const socketURL = environment.SIPwsURL;
    const userURI = `sip:${s.sipUser}@${s.sipDomain}`;
    const password = s.sipUser; // s.sipPassword; <-- set to null by service gateway on retrieval
    this.jsSIPService.register(socketURL, userURI, password, this.local, this.remote);

    this.subscribeToJSSIPEvents();
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  private subscribeToJSSIPEvents() {
    this.jsSIPService.onIncomingCall.pipe(takeUntil(this.destroyed)).subscribe(callee => {
      if (callee) {
        this.callee = callee;
        this.changeState(State.INCOMING);
      }
    });

    this.jsSIPService.onSessionEnded.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.audioPlayer.play('bye');
      this.changeState(State.NO_SESSION);
    });

    this.jsSIPService.onSessionAccepted.pipe(takeUntil(this.destroyed)).subscribe(() => this.changeState(State.ONGOING));

    this.jsSIPService.onSessionProgress.pipe(takeUntil(this.destroyed)).subscribe(() => {});

    this.jsSIPService.onSendingInvite.pipe(takeUntil(this.destroyed)).subscribe(callTarget => {
      this.changeState(State.OUTGOING);
      this.callee = callTarget;
    });

    this.jsSIPService.error.pipe(takeUntil(this.destroyed)).subscribe(error => {
      this.notificationService.errorNotification.next(error);
      this.changeState(State.NO_SESSION);
    });
  }

  private updateCurrentStatus(contact: EndUser, absenceEntries: AbsenceEntry[]) {
    if (contact && contact.absenceId) {
      const absenceEntry = absenceEntries.find(ae => {
        return ae.id === contact.absenceId;
      });
      if (absenceEntry) {
        this.setCurrentStatus(absenceEntry);
      }
    } else {
      this.setCurrentStatus(null);
    }
  }

  private setCurrentStatus(current: AbsenceEntry): void {
    if (!current) {
      this.currentAbsenceReason = this.statusModels.find(r => r.reasonId === 0) as StatusAvailableModel;
      return;
    }
    this.currentAbsenceReason = this.statusModels.find(e => current.reasonId === e.reasonId) as StatusAbsentModel;
    this.currentAbsenceReason.endTime = new Date(current.endTime);
    this.currentAbsenceReason.absenceId = current.id;
  }

  onAccept() {
    this.jsSIPService.accept();
  }

  onCall(outgoingNr: string) {
    this.jsSIPService.call(outgoingNr);
    this.callee = outgoingNr;
  }

  onHangUp() {
    this.jsSIPService.hangUp();
  }

  private setTimer() {
    this.duration++;
  }

  changeState(newState: State) {
    if (newState === State.OUTGOING) {
      this.audioPlayer.play('calling');
    } else {
      this.audioPlayer.stop('calling');
    }

    if (newState === State.NO_SESSION) {
      if (this.timer) {
        clearInterval(this.timer);
        this.duration = 0;
        this.timer = undefined;
      }
      this.onHold = false;
      this.muted = false;
      this.dtmfMessage = '';
      this.callee = '';
      this.outgoingNr = '';
      this.dialpadVisible = false;
    }

    if (newState === State.ONGOING) {
      this.timer = setInterval(this.setTimer.bind(this), 1000);
    }

    if (newState === State.INCOMING) {
      this.audioPlayer.play('ringing');
    } else {
      this.audioPlayer.stop('ringing');
    }

    this.currentState = newState;
  }

  toggleDialPadVisible() {
    this.dialpadVisible = !this.dialpadVisible;
  }

  toggleMuted() {
    this.muted = !this.muted;
    this.muted ? this.jsSIPService.mute() : this.jsSIPService.unMute();
  }

  toggleHold() {
    this.onHold = !this.onHold;
    this.onHold ? this.jsSIPService.hold() : this.jsSIPService.unHold();
  }

  toggleContactShown() {
    this.callLogShown = false;
    this.dialerShown = false;
    this.contactsShown = true;
  }

  toggleCallLogShown() {
    this.contactsShown = false;
    this.dialerShown = false;
    this.callLogShown = true;
  }

  toggleDialerShown() {
    this.contactsShown = false;
    this.callLogShown = false;
    this.dialerShown = true;
  }

  sendDTMF(tone) {
    this.dtmfMessage = this.dtmfMessage + tone;
    this.jsSIPService.sendDTMF(tone);
  }

  addToOutgoingNr(value: string) {
    this.outgoingNr += value;
  }

  removeFromOutgoingNumber() {
    if (this.outgoingNr.length > 0) {
      this.outgoingNr = this.outgoingNr.substring(0, this.outgoingNr.length - 1);
    }
  }

  get currentAbsenceEndTime() {
    return 'endTime' in this.currentAbsenceReason ? this.currentAbsenceReason.endTime : undefined;
  }
}

export enum State {
  INCOMING,
  ONGOING,
  OUTGOING,
  NO_SESSION
}
